import { computed, Injectable, signal } from '@angular/core';
import {
  IAgenda,
  IAgendamento,
  IEstabelecimentoClienteEntity,
  IFeriado,
  IProfissional,
  StatusAgendamentoEnum,
} from '@nomoh-monorepo/shared-types';

type AgendaModel = IAgenda;
type AgendamentoModel = IAgendamento;
type FeriadoModel = IFeriado & { status?: number };

const createEmptyAgenda = (): AgendaModel => ({
  id: '',
  autoAgendamento: false,
  agendamentos: [],
});

/**
 * DataService para gerenciar estado de Agendas com Signals
 * Segue o padrão do monorepo (ver: libs/data-access/nomoh-administrador/feature-planos)
 *
 * ✅ Usa interfaces/modelos compartilhados do data-access
 */
@Injectable({ providedIn: 'root' })
export class AgendaDataService {
  // Estado base vindo do banco
  private _agendaInDb = signal<AgendaModel>(createEmptyAgenda());
  private _agenda = signal<AgendaModel>(createEmptyAgenda());

  // Seleção/data
  private _dataSelecionada = signal<Date>(new Date());

  // Modais

  // Filtros e contadores
  private _filtroPreenchido = signal(false);
  private _visualizarDiasConfirmados = signal(true);
  private _contadorAgendados = signal(0);

  // Agendamentos/Feriados
  private _agendamentos = signal<AgendamentoModel[]>([]);
  private _feriados = signal<FeriadoModel[]>([]);
  private _feriadosInDb = signal<FeriadoModel[]>([]);

  // Modal informação agendamento
  private _agendamentoParaModal = signal<IAgendamento | null>(null);

  // Estado de carregamento/erro
  private _loading = signal(false);
  private _isLoadingModalInformacao = signal(false);
  private _erro = signal<string | null>(null);

  // Signals públicos
  $agendaInDb = this._agendaInDb.asReadonly();
  $agenda = this._agenda.asReadonly();
  $dataSelecionada = this._dataSelecionada.asReadonly();
  $filtroPreenchido = this._filtroPreenchido.asReadonly();
  $visualizarDiasConfirmados = this._visualizarDiasConfirmados.asReadonly();
  $contadorAgendados = this._contadorAgendados.asReadonly();
  $agendamentos = this._agendamentos.asReadonly();
  $feriados = this._feriados.asReadonly();
  $feriadosInDb = this._feriadosInDb.asReadonly();
  $agendamentoParaModal = this._agendamentoParaModal.asReadonly();
  $loading = this._loading.asReadonly();
  $isLoadingModalInformacao = this._isLoadingModalInformacao.asReadonly();
  $erro = this._erro.asReadonly();

  // Computed helpers
  $temAgendamentos = computed(() => this._agendamentos().length > 0);
  $agendamentosPorStatus = computed(() => {
    const agendamentos = this._agendamentos();
    return {
      pendentes: agendamentos.filter(
        (a) => a.status === StatusAgendamentoEnum.PENDENTE
      ),
      confirmados: agendamentos.filter(
        (a) => a.status === StatusAgendamentoEnum.CONFIRMADO
      ),
      cancelados: agendamentos.filter(
        (a) => a.status === StatusAgendamentoEnum.CANCELADO
      ),
      realizados: agendamentos.filter(
        (a) => a.status === StatusAgendamentoEnum.REALIZADO
      ),
      agendados: agendamentos.filter(
        (a) => a.status === StatusAgendamentoEnum.PENDENTE
      ),
    };
  });
  $totalPendentes = computed(
    () => this.$agendamentosPorStatus().pendentes.length
  );

  // Mutations
  setAgendaInDb(agenda: AgendaModel) {
    this._agendaInDb.set(agenda);
  }

  setAgenda(agenda: AgendaModel) {
    this._agenda.set(agenda);
  }

  setDataSelecionada(data: Date) {
    this._dataSelecionada.set(data);
  }

  setFiltroPreenchido(preenchido: boolean) {
    this._filtroPreenchido.set(preenchido);
  }

  setAgendamentos(agendamentos: AgendamentoModel[]) {
    this._agendamentos.set(agendamentos);
  }

  addAgendamento(agendamento: AgendamentoModel) {
    this._agendamentos.update((current) => [...current, agendamento]);
  }

  updateAgendamento(id: string, agendamento: Partial<AgendamentoModel>) {
    this._agendamentos.update((current) =>
      current.map((item) =>
        item.id === id ? { ...item, ...agendamento } : item
      )
    );
  }

  removeAgendamento(id: string) {
    this._agendamentos.update((current) => current.filter((a) => a.id !== id));
  }

  atualizarClienteNosAgendamentos(
    clienteId: string,
    data: Partial<IEstabelecimentoClienteEntity>
  ) {
    if (!clienteId) return;
    const targetId = clienteId.toString().trim().toLowerCase();

    const updateFn = (agendamento: AgendamentoModel): AgendamentoModel => {
      const extendido = agendamento as AgendamentoModel & {
        cliente?: Partial<IEstabelecimentoClienteEntity>;
      };
      const cId = extendido.clienteId?.toString().trim().toLowerCase();
      const entityId = extendido.cliente?.id?.toString().trim().toLowerCase();
      const pId = extendido.cliente?.pessoaId?.toString().trim().toLowerCase();
      const dataPId = data.pessoaId?.toString().trim().toLowerCase();

      if (
        cId === targetId ||
        entityId === targetId ||
        (dataPId && (cId === dataPId || pId === dataPId))
      ) {
        const clienteAtualizado = {
          ...(extendido.cliente || { id: clienteId }),
          ...data,
        };
        const novoNome =
          data.nome !== undefined
            ? data.nome
            : extendido.cliente?.nome || extendido.nomeCliente;
        const novoTelefone =
          data.telefone !== undefined
            ? data.telefone
            : extendido.cliente?.telefone || extendido.telefoneCliente;

        return {
          ...extendido,
          cliente: clienteAtualizado as IEstabelecimentoClienteEntity,
          nomeCliente: novoNome,
          telefoneCliente: novoTelefone,
        };
      }
      return agendamento;
    };

    this._agendaInDb.update((agenda) => ({
      ...agenda,
      agendamentos: agenda.agendamentos?.map(updateFn),
    }));

    this._agenda.update((agenda) => ({
      ...agenda,
      agendamentos: agenda.agendamentos?.map(updateFn),
    }));

    this._agendamentos.update((current) => current.map(updateFn));

    const modalAg = this._agendamentoParaModal();
    if (modalAg) {
      const modalCId = modalAg.clienteId?.toString().trim().toLowerCase();
      const modalEntityId = (modalAg as unknown as { cliente?: { id?: string } }).cliente?.id?.toString().trim().toLowerCase();
      if (modalCId === targetId || modalEntityId === targetId) {
        this._agendamentoParaModal.set(updateFn(modalAg));
      }
    }
  }

  atualizarProfissionalNosAgendamentos(
    profissionalId: string,
    data: Partial<IProfissional>
  ) {
    if (!profissionalId) return;
    const targetId = profissionalId.toString().trim().toLowerCase();

    const updateFn = (agendamento: AgendamentoModel): AgendamentoModel => {
      const extendido = agendamento as AgendamentoModel & {
        profissional?: Partial<IProfissional>;
      };
      const pId = extendido.profissionalId?.toString().trim().toLowerCase();
      const entityId = extendido.profissional?.id?.toString().trim().toLowerCase();

      if (pId === targetId || entityId === targetId) {
        const profissionalAtualizado = {
          ...(extendido.profissional || { id: profissionalId }),
          ...data,
        };
        return {
          ...extendido,
          profissional: profissionalAtualizado as IProfissional,
        };
      }
      return agendamento;
    };

    this._agendaInDb.update((agenda) => ({
      ...agenda,
      agendamentos: agenda.agendamentos?.map(updateFn),
    }));

    this._agenda.update((agenda) => ({
      ...agenda,
      agendamentos: agenda.agendamentos?.map(updateFn),
    }));

    this._agendamentos.update((current) => current.map(updateFn));

    const modalAg = this._agendamentoParaModal();
    if (modalAg) {
      const modalPId = modalAg.profissionalId?.toString().trim().toLowerCase();
      const modalEntityId = (modalAg as unknown as { profissional?: { id?: string } }).profissional?.id?.toString().trim().toLowerCase();
      if (modalPId === targetId || modalEntityId === targetId) {
        this._agendamentoParaModal.set(updateFn(modalAg));
      }
    }
  }

  setFeriados(feriados: FeriadoModel[]) {
    this._feriados.set(feriados);
  }

  setFeriadosInDb(feriados: FeriadoModel[]) {
    this._feriadosInDb.set(feriados);
  }

  setVisualizarDiasConfirmados(visualizar: boolean) {
    this._visualizarDiasConfirmados.set(visualizar);
  }

  setAtualizarContadorAgendados(count: number) {
    this._contadorAgendados.set(count);
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setAgendamentoParaModal(agendamento: IAgendamento | null) {
    this._agendamentoParaModal.set(agendamento);
  }

  setIsLoadingModalInformacao(loading: boolean) {
    this._isLoadingModalInformacao.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  limparErro() {
    this._erro.set(null);
  }

  private readonly _desabilitarNovoAgendamento = signal<boolean>(false);
  $desabilitarNovoAgendamento = this._desabilitarNovoAgendamento.asReadonly();
  setDesabilitarNovoAgendamento(value: boolean) {
    this._desabilitarNovoAgendamento.set(value);
  }

  reset(): void {
    this._agendaInDb.set(createEmptyAgenda());
    this._agenda.set(createEmptyAgenda());
    this._dataSelecionada.set(new Date());
    this._filtroPreenchido.set(false);
    this._agendamentos.set([]);
    this._feriados.set([]);
    this._feriadosInDb.set([]);
    this._visualizarDiasConfirmados.set(true);
    this._contadorAgendados.set(0);
    this._loading.set(false);
    this._isLoadingModalInformacao.set(false);
    this._erro.set(null);
    this._desabilitarNovoAgendamento.set(false);
  }
}
